import type {
  ReportAmendmentNoticeFragmentConfig,
  ReportCorrectionNoticeFragmentConfig,
  ReportSupplementEntry,
  ReportSupplementNoticeFragmentConfig,
} from "../../types/reportTemplate";
import { resolveRichTextForDisplay } from "../../utils/helpers";
import {
  buildRichTextReportFragments,
  exceedsReportFragmentPackingLimits,
} from "./addendumReportFragments";
import { buildCorrectionDescriptions } from "./reportCorrectionContent";
import { compareReportSupplementEntries } from "./reportSupplementGroups";
import type { ReportSupplementGroup } from "./reportSupplementGroups";

interface SupplementEntryFragmentContext {
  entry: ReportSupplementEntry;
  entryIndex: number;
}

interface SupplementEntryFragmentPresentation {
  showBanner: boolean;
  showDivider: boolean;
  showAttribution: boolean;
}

export function buildReportSupplementNoticeFragments(
  groups: ReportSupplementGroup[]
): ReportSupplementNoticeFragmentConfig[] {
  const entries = groups
    .flatMap((group) => group.entries)
    .sort(compareReportSupplementEntries);
  const byId = new Map(entries.map((entry, index) => [entry.id, index]));
  return groups
    .flatMap(buildGroupFragments)
    .sort(
      (left, right) =>
        ("entryId" in left && left.entryId
          ? byId.get(left.entryId) ?? -1
          : -1) -
        ("entryId" in right && right.entryId
          ? byId.get(right.entryId) ?? -1
          : -1)
    );
}

function buildGroupFragments(
  group: ReportSupplementGroup
): ReportSupplementNoticeFragmentConfig[] {
  if (group.type === "addendum") return [buildAddendumBannerFragment()];
  if (group.type === "amended") {
    return group.entries.flatMap((entry, entryIndex) =>
      buildAmendmentFragments({ entry, entryIndex })
    );
  }
  return group.entries.flatMap((entry, entryIndex) =>
    buildCorrectionFragments({ entry, entryIndex })
  );
}

function buildAddendumBannerFragment(): ReportSupplementNoticeFragmentConfig {
  return {
    groupType: "addendum",
    showBanner: true,
    showDivider: false,
    showAttribution: false,
  };
}

function buildAmendmentFragments(
  context: SupplementEntryFragmentContext
): ReportAmendmentNoticeFragmentConfig[] {
  const contentHtml = resolveRichTextForDisplay(
    context.entry.contentHtml ||
      context.entry.comments ||
      context.entry.amendmentReason ||
      "",
    {}
  );
  const fragments = buildRichTextReportFragments(contentHtml);
  const reasonStartIndex = fragments.findIndex(
    (fragment) => fragment.textContent.trim().length > 0
  );
  return fragments.map((fragment, fragmentIndex) => ({
    ...buildEntryFragmentPresentation(
      context.entryIndex,
      fragmentIndex,
      fragments.length
    ),
    groupType: "amended",
    entryId: context.entry.id,
    contentHtml: fragment.contentHtml,
    showReasonHeading: fragmentIndex === reasonStartIndex,
  }));
}

function buildCorrectionFragments(
  context: SupplementEntryFragmentContext
): ReportCorrectionNoticeFragmentConfig[] {
  const descriptions = buildCorrectionDescriptions(context.entry);
  const fragments = fragmentCorrectionDescriptions(descriptions);
  return fragments.map((correctionDescriptions, fragmentIndex) => ({
    ...buildEntryFragmentPresentation(
      context.entryIndex,
      fragmentIndex,
      fragments.length
    ),
    groupType: "corrected",
    entryId: context.entry.id,
    correctionDescriptions,
    correctionUsesList: descriptions.length > 1,
  }));
}

function buildEntryFragmentPresentation(
  entryIndex: number,
  fragmentIndex: number,
  fragmentCount: number
): SupplementEntryFragmentPresentation {
  return {
    showBanner: entryIndex === 0 && fragmentIndex === 0,
    showDivider: entryIndex > 0 && fragmentIndex === 0,
    showAttribution: fragmentIndex === fragmentCount - 1,
  };
}

function fragmentCorrectionDescriptions(descriptions: string[]): string[][] {
  const totalCharacters = descriptions.reduce(
    (total, description) => total + description.length,
    0
  );
  if (
    !exceedsReportFragmentPackingLimits(descriptions.length, totalCharacters)
  ) {
    return [descriptions];
  }
  return descriptions.flatMap(splitCorrectionDescription);
}

function splitCorrectionDescription(description: string): string[][] {
  if (!exceedsReportFragmentPackingLimits(1, description.length)) {
    return [[description]];
  }
  const paragraph = document.createElement("p");
  paragraph.textContent = description;
  return buildRichTextReportFragments(paragraph.outerHTML).map((fragment) => [
    fragment.textContent,
  ]);
}
